<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useAuthState } from '~/composables/useAuthState'
import { useLocale } from '~/composables/useLocale'
import PointsHistoryPopup from '~/components/PointsHistoryPopup.vue'
import MmMenu from '../base/MmMenu.vue'
import MmIcon from '../base/MmIcon.vue'
import type { MmMenuItem } from '../base/menu'
import { useCreator } from '../creator/useCreator'
import CreatorCelebration from '../creator/CreatorCelebration.vue'
import IncentiveResults from '../creator/IncentiveResults.vue'
const props = defineProps<{ mobile?: boolean }>()
const { t } = useI18n(), colorMode = useColorMode(), route = useRoute()
const { user, isAuthenticated, isLoggingIn, handleSignIn, handleLogout, totalPoints, userAccount } = useAuthState()
const { getCurrentLocale, setLocale, availableLocales } = useLocale()
const { profile } = useCreator()
const localePath = useLocalePath()
const open = ref(false), pointsOpen = ref(false), language = ref(false)
const email = computed(() => user.value?.primaryEmailAddress?.emailAddress || '')
const initial = computed(() => (profile.value?.display_name || user.value?.firstName || t('ux.you')).slice(0, 1).toUpperCase())
const items = computed<MmMenuItem[]>(() => language.value ? [
  { head: t('settings.language') },
  ...availableLocales.map(item => ({ id: `locale:${item.code}`, label: item.name, checked: item.code === getCurrentLocale() })),
] : [
  ...(isAuthenticated.value ? [
    { head: t('mm.fidelity.signedInAs', { name: email.value }) },
    { id: 'me', label: t('mm.shell.me'), icon: 'user' as const, to: '/me' },
    { id: 'rewards', label: t('incRewards.title'), icon: 'gift' as const, to: localePath('/rewards') },
    { id: 'points', label: t('t24a.creditsHistory'), icon: 'zap' as const, meta: String(userAccount.value ? totalPoints.value : 0) },
    { id: 'billing', label: t('t24a.subscriptionBilling'), icon: 'card' as const, to: '/billing' },
  ] : [{ id: 'signin', label: t('mm.shell.signIn'), icon: 'user' as const, disabled: isLoggingIn.value }]),
  { id: 'language', label: t('settings.language'), icon: 'globe', meta: availableLocales.find(item => item.code === getCurrentLocale())?.name, keepOpen: true },
  { id: 'theme', label: t('mm.fidelity.switchTheme'), icon: colorMode.value === 'light' ? 'moon' : 'sun' },
  { divider: true },
  { id: 'app', label: t('t24a.getApp'), icon: 'phone', meta: 'iOS · Android', to: '/about#home' },
  { id: 'extension', label: t('t24a.chromeExtension'), icon: 'puzzle', meta: 'YouTube → MP3', to: '/extension' },
  { id: 'guides', label: t('t24a.guidesHelp'), icon: 'help', to: '/guides' },
  ...(isAuthenticated.value ? [{ divider: true as const }, { id: 'logout', label: t('t24a.signOut'), icon: 'logout' as const }] : []),
])
const select = async (item: Extract<MmMenuItem, { id: string }>) => {
  if (item.id === 'language') { language.value = true; return }
  if (item.id.startsWith('locale:')) setLocale(item.id.slice(7))
  await nextTick(); await nextTick()
  if (item.id === 'theme') colorMode.preference = colorMode.value === 'dark' ? 'light' : 'dark'
  if (item.id === 'points') pointsOpen.value = true
  if (item.id === 'signin') handleSignIn()
  if (item.id === 'logout') await handleLogout()
}
watch(open, value => { if (!value) language.value = false })
watch([() => route.fullPath, () => user.value?.id], () => { open.value = false; pointsOpen.value = false })
defineExpose({ openPoints: () => { pointsOpen.value = true } })
</script>

<template>
  <MmMenu v-model="open" :label="t('t24a.accountMenu')" :items="items" :mobile-title="mobile && isAuthenticated && !language ? email : undefined" @select="select">
    <template #trigger="{ toggle, expanded, id }">
      <button type="button" :class="mobile ? 'tab-me' : 'avatar-btn'" :aria-label="mobile ? t('mm.shell.me') : t('t24a.accountMenu')" :aria-current="mobile && route.path === '/pricing' ? 'page' : undefined" aria-haspopup="menu" :aria-expanded="expanded" :aria-controls="expanded ? id : undefined" @click="toggle">
        <template v-if="mobile"><MmIcon name="user" :size="22" /><span>{{ t('mm.shell.me') }}</span></template>
        <span v-else class="avatar"><img v-if="profile?.avatar?.url" :src="profile.avatar.url" alt="" width="36" height="36"><template v-else>{{ initial }}</template></span>
      </button>
    </template>
  </MmMenu>
  <PointsHistoryPopup v-model:visible="pointsOpen" />
  <CreatorCelebration v-if="!mobile" />
  <IncentiveResults v-if="!mobile" />
</template>
